// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import FormatPermissions from '@Molecules/Format/FormatPermissions/FormatPermissions';
import { ADMIN_ORG_ID } from '@Types';
import { FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { UserType, UserTypeValues, ProfileOutput } from 'thehive-sdk';
import useListQuery from '@Hooks/useListQuery';
import client, { queryClient } from '@Client';
import { computeListSelector } from '@Organisms/Profile/selector';

export interface UserValuesForm extends FormSubmitOptions {
    login: string;
    name: string;
    organisation: string;
    profile: string;
    type: UserType;
}

interface UserFormProps {
    form: FormInstance;
    orgId: string;
    isValid: boolean;
    onFinish(values: UserValuesForm): void;
    onChange: () => void;
    onCancel: () => void;
}

export const UserForm = ({ form, orgId, isValid, onFinish, onChange, onCancel }: UserFormProps): ReactElement => {
    const i = useLocalMessage();

    const { data: listAllProfilesData = [] } = useListQuery<ProfileOutput[]>({
        controller: client.ProfileController,
        queryParams: {
            selector: computeListSelector(),
        },
        manual: true,
        fetchOnStart: true,
    });

    // Start duplicated code from /src/components/organisms/User/GlobaUserCreate/GlobalUserForm.tsx:33
    queryClient.refetchQueries(['getStatus']); // Ugly way to prevent refetch waiting time

    const userTypes = UserTypeValues.reduce((acc, value) => {
        return [...acc, { label: value, value }];
    }, [] as { label: string; value: string }[]);
    // End duplicated code

    const profilesList = listAllProfilesData.filter((profile) =>
        orgId === ADMIN_ORG_ID ? profile.forAdmin : profile.forOrg,
    );

    return (
        <THForm<UserValuesForm>
            name="user-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onChange}
            initialValues={{ organisation: orgId, type: userTypes?.find(Boolean)?.label }}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        disabled={!isValid}
                        onCancel={onCancel}
                        onAnother={() => {
                            form.setFieldsValue({
                                another: true,
                            });
                            form.submit();
                        }}
                        formName="user-form"
                    />
                }
            >
                <THForm.Item
                    name="type"
                    label={i('user.create.form.type.label')}
                    help={i('user.create.form.type.help')}
                >
                    <THSelect options={userTypes} />
                </THForm.Item>

                <THForm.Item label={i('user.create.form.organisation.label')} name="organisation">
                    <THInput disabled />
                </THForm.Item>

                <THForm.Item
                    label={i('user.create.form.login.label')}
                    name="login"
                    rules={[{ required: true, type: 'email', message: i('user.create.form.login.required') }]}
                >
                    <THInput placeholder={i('user.create.form.login.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    label={i('user.create.form.name.label')}
                    name="name"
                    rules={[{ required: true, message: i('user.create.form.name.required') }]}
                >
                    <THInput placeholder={i('user.create.form.name.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    name="profile"
                    label={i('user.create.form.profile.label')}
                    rules={[{ required: true, message: i('user.create.form.profile.required') }]}
                >
                    <THSelect
                        options={profilesList.map(({ name }) => ({ label: name, value: name }))}
                        placeholder={i('user.create.form.profile.placeholder')}
                    />
                </THForm.Item>

                <THForm.Item<UserValuesForm> noStyle shouldUpdate={(prev, curr) => prev.profile !== curr.profile}>
                    {({ getFieldValue }) => {
                        const newPermissions =
                            profilesList.find((profile) => profile.name === getFieldValue('profile'))?.permissions ||
                            [];

                        return (
                            <THForm.Item
                                label={i('user.create.form.permissions.label')}
                                hidden={!getFieldValue('profile')}
                            >
                                <FormatPermissions permissions={newPermissions} permissionsOnly />
                            </THForm.Item>
                        );
                    }}
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
